import {
    EM_DASH,
    UNION_SALE_MAX_RANGE_DAYS,
    UNION_SALE_UPSTREAM_MESSAGE,
    UNION_SALE_UPSTREAM_PREFIX,
} from "../../constants/unionSale";

const SHORT_MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export const ALL_CODE = "ALL";
export const ALL_UNION_OPTION = { code: ALL_CODE, desc: "All Union" };
export const ALL_DISTRICT_OPTION = { code: ALL_CODE, desc: "All District" };
export const ALL_DISTRIBUTOR_OPTION = { code: ALL_CODE, desc: "All Distributor" };

export const toIsoDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export const lastNDays = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - (days - 1));
    return { fromDate: toIsoDate(start), toDate: toIsoDate(end) };
};

export const DATE_PRESETS = [
    { code: "last7days", desc: "Last 7 Days", days: 7 },
    { code: "last1month", desc: "Last 1 Month", days: 30 },
    { code: "last6months", desc: "Last 6 Months", days: 180 },
];

export const CUSTOM_RANGE_CODE = "custom";
export const DEFAULT_RANGE_CODE = "last1month";
export const EMPTY_RANGE = { fromDate: "", toDate: "" };

export const resolveRange = (code, custom) => {
    if (code === CUSTOM_RANGE_CODE) {
        const fromDate = custom?.fromDate || "";
        const toDate = custom?.toDate || fromDate;
        return { fromDate, toDate };
    }
    const preset = DATE_PRESETS.find((item) => item.code === code);
    return lastNDays(preset?.days ?? 30);
};

export const rangeDays = ({ fromDate, toDate } = {}) => {
    if (!fromDate || !toDate) return 0;
    const start = new Date(`${fromDate}T00:00:00`);
    const end = new Date(`${toDate}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
    return Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
};

export const sameRange = (a, b) =>
    Boolean(a?.fromDate) && a.fromDate === b?.fromDate && a.toDate === b?.toDate;

export const buildRangeKey = ({ fromDate, toDate } = {}) => `${fromDate}|${toDate}`;

// The 31-day cap applies to every endpoint except /trend.
export const validateRange = (range) => {
    const fromDate = range?.fromDate;
    const toDate = range?.toDate;
    if (!fromDate || !toDate) return "Select a from and to date to load sale figures.";
    if (fromDate > toDate) return "The from date must not be after the to date.";
    const days = rangeDays(range);
    if (days > UNION_SALE_MAX_RANGE_DAYS) {
        return `Date range too wide: ${days} days (max ${UNION_SALE_MAX_RANGE_DAYS}).`;
    }
    return "";
};

export const formatDateShort = (iso) => {
    if (!iso) return "";
    const [, month, day] = String(iso).split("-");
    if (!month || !day) return iso;
    return `${Number(day)} ${SHORT_MONTHS[Number(month) - 1] ?? ""}`.trim();
};

export const buildRangeLabel = ({ fromDate, toDate } = {}) => {
    if (!fromDate && !toDate) return "";
    if (!toDate || fromDate === toDate) return formatDateShort(fromDate);
    return `${formatDateShort(fromDate)} - ${formatDateShort(toDate)}`;
};

export const formatBucketLabel = (iso, bucket) => {
    if (!iso) return "";
    const [year, month, day] = String(iso).split("-");
    const shortMonth = SHORT_MONTHS[Number(month) - 1] ?? "";
    if (bucket === "month") return `${shortMonth} ${String(year).slice(-2)}`;
    if (bucket === "week") return `W ${Number(day)} ${shortMonth}`;
    return formatDateShort(iso);
};

const isBlank = (value) =>
    value === null || value === undefined || value === "" || Number.isNaN(Number(value));

const indianFormatter = (fractionDigits) =>
    new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });

export const formatQty = (value) =>
    isBlank(value) ? EM_DASH : indianFormatter(2).format(Number(value));

export const formatCurrency = (value) => {
    if (isBlank(value)) return EM_DASH;
    const amount = Number(value);
    const fractionDigits = Math.abs(amount) >= 100000 ? 0 : 2;
    return `₹${indianFormatter(fractionDigits).format(amount)}`;
};

export const formatPercent = (value) =>
    isBlank(value) ? EM_DASH : `${Math.abs(Number(value)).toFixed(2)}%`;

export const formatSignedPercent = (value) => {
    if (isBlank(value)) return EM_DASH;
    const amount = Number(value);
    return `${amount >= 0 ? "▲" : "▼"} ${Math.abs(amount).toFixed(2)}%`;
};

export const formatCompactIndian = (value) => {
    if (isBlank(value)) return "";
    const amount = Number(value);
    const abs = Math.abs(amount);
    if (abs >= 10000000) return `${(amount / 10000000).toFixed(abs >= 100000000 ? 0 : 1)} Cr`;
    if (abs >= 100000) return `${(amount / 100000).toFixed(abs >= 1000000 ? 0 : 1)} Lk`;
    if (abs >= 1000) return `${Math.round(amount / 1000)}K`;
    return indianFormatter(0).format(amount);
};

export const growthDirection = (value) => {
    if (isBlank(value)) return null;
    return Number(value) >= 0 ? "ins" : "dec";
};


export const buildUnionOptions = (scopesData) => {
    const options = [...(scopesData?.scopes ?? [])]
        .sort((a, b) => (a.dispOrder ?? 0) - (b.dispOrder ?? 0))
        .map((scope) => ({
            code: String(scope.id),
            desc: scope.displayName || `${scope.unionName} - ${scope.unit}`,
        }));

    return [ALL_UNION_OPTION, ...options];
};

export const scopeById = (scopesData, scopeId) => {
    if (isBlank(scopeId)) return null;
    return (
        (scopesData?.scopes ?? []).find((scope) => Number(scope.id) === Number(scopeId)) ?? null
    );
};

export const buildDistrictOptions = (distributorsData) => {
    const districts = distributorsData?.masterDistricts ?? [];
    return [ALL_DISTRICT_OPTION, ...districts.map((name) => ({ code: name, desc: name }))];
};

export const buildDistributorOptions = (distributorsData, { district, unionName, unit } = {}) => {
    const rows = distributorsData?.distributors ?? [];
    const wantUnit = String(unit ?? "").toUpperCase();
    const filtered = rows.filter((row) => {
        if (district && row?.districtName !== district) return false;
        if (unionName && row?.unionName !== unionName) return false;
        if (wantUnit && String(row?.unit ?? "").toUpperCase() !== wantUnit) return false;
        return true;
    });
    return [
        ALL_DISTRIBUTOR_OPTION,
        ...filtered.map((row) => ({
            code: row.distributorId,
            desc: row.distributorName || row.distributorId,
        })),
    ];
};


export const isSelectedScope = (row, scope) =>
    Boolean(scope?.displayName) && row?.displayName === scope.displayName;

const TT_UNION_SVG = `<svg class="vts-tt-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 21V9l9-6 9 6v12"/><path d="M9 21v-6h6v6"/></svg>`;
const TT_DISTRICT_SVG = `<svg class="vts-tt-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 21s7-6.5 7-11a7 7 0 1 0-14 0c0 4.5 7 11 7 11z"/><circle cx="12" cy="10" r="2.5"/></svg>`;
const TT_MILK_SVG = `<svg class="vts-tt-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"><path d="M9 2h6"/><path d="M9 2v3L6 9v11a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V9l-3-4V2"/><path d="M6 13h12"/></svg>`;
const TT_VALUE_SVG = `<svg class="vts-tt-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M7 5h9"/><path d="M7 9h9"/><path d="M14 5c0 5-7 4-7 4l7 9"/></svg>`;

const escapeHtml = (value) =>
    String(value ?? "").replaceAll(
        /[&<>"']/g,
        (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]
    );

const buildDistributorTooltipHtml = (row, name, formatValue) => `
    <div class="vts-tt-title">${escapeHtml(name)}</div>
    <div class="vts-tt-row">${TT_UNION_SVG} ${escapeHtml(row.unionName || EM_DASH)}</div>
    <div class="vts-tt-row">${TT_DISTRICT_SVG} ${escapeHtml(row.districtName || EM_DASH)}</div>
    <div class="vts-tt-row">${TT_MILK_SVG} ${isBlank(row.milkQty) ? EM_DASH : `${escapeHtml(formatQty(row.milkQty))} Ltr`}</div>
    <div class="vts-tt-row">${TT_VALUE_SVG} ${escapeHtml(formatValue(row.totalSaleValueRs))}</div>
`;

export const buildProductSlices = (topProducts) => {
    const top = topProducts?.top ?? [];
    const slices = top.map((group) => ({
        key: group.key,
        label: group.label || group.key,
        value: group.productValue,
        sharePct: group.sharePct,
    }));

    const others = topProducts?.others;
    if (!others) return slices;

    return [
        ...slices,
        {
            key: "__others__",
            label: others.label || "Others",
            value: others.productValue,
            sharePct: others.sharePct,
            isOthers: true,
        },
    ];
};

export const buildDistributorMarkers = (rows, formatValue, nameById) =>
    (rows ?? [])
        .filter((row) => row?.plottable && !isBlank(row?.latitude) && !isBlank(row?.longitude))
        .map((row) => {
            const name = nameById?.[row.distributorId] || row.distributorName || row.distributorId;
            return {
                id: row.distributorId,
                lat: Number(row.latitude),
                lng: Number(row.longitude),
                color: "#4B51B3",
                tooltip: buildDistributorTooltipHtml(row, name, formatValue),
            };
        });

export const buildDistributorNameMap = (distributorsData) =>
    Object.fromEntries(
        (distributorsData?.distributors ?? [])
            .filter((row) => row?.distributorId && row?.distributorName)
            .map((row) => [row.distributorId, row.distributorName])
    );

export const buildTrendSeries = (points, valueKey, bucket) =>
    (points ?? []).map((point) => ({
        label: formatBucketLabel(point?.bucketStart, bucket),
        value: point?.hasData ? point?.[valueKey] ?? null : null,
    }));

export const hasPlottableValue = (series) =>
    (series ?? []).some((point) => !isBlank(point?.value));

const VALIDATION_MARKERS = ["fromDate", "toDate", "Date range too wide"];

export const normalizeApiError = (error) => {
    const status = Number(error?.statusCode ?? error?.response?.status ?? 0);
    const raw = typeof error?.message === "string" ? error.message : "";

    if (status === 400 && raw.startsWith(UNION_SALE_UPSTREAM_PREFIX)) {
        return { status, kind: "upstream", message: UNION_SALE_UPSTREAM_MESSAGE };
    }
    if (status === 400 && VALIDATION_MARKERS.some((marker) => raw.includes(marker))) {
        return { status, kind: "validation", message: raw };
    }
    if (status === 400) {
        return { status, kind: "mapping", message: raw || "Invalid request." };
    }
    if (!status) {
        return {
            status,
            kind: "network",
            message: "Unable to reach the server. Check your connection and try again.",
        };
    }
    return { status, kind: "unknown", message: raw || "Something went wrong. Please try again." };
};
